import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Campaign } from "../../types/sponsorships/campaigns";
import { formatNumber } from "../../utils/helpers";
import instagramLogo from "../../assets/icons/instagram-logo.svg";
import youtubeLogo from "../../assets/icons/youtube-logo.svg";
import { getSeatsLabel, getTier } from "./sponsorshipCampaignShared";
import "./style.scss";
import CreatorCategoryIcon from "../../assets/icons/CreatorCategoryIcon";
import { toNumber } from "lodash";

const formatCountdownHms = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
};

const parseOpensAt = (value?: string | number | Date | null): Date | null => {
    if (value == null) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const EarlyAccessTimer: React.FC<{ target?: string | number | Date | null }> = ({ target }) => {
    const parsed = useMemo(() => parseOpensAt(target), [target]);
    const [, setTick] = useState(0);

    useEffect(() => {
        if (!parsed) return undefined;
        const id = window.setInterval(() => setTick((t) => t + 1), 1000);
        return () => window.clearInterval(id);
    }, [parsed]);

    if (!parsed) return null;

    const now = Date.now();
    if (parsed.getTime() <= now) {
        return (
            <p className="sponsorship-card-early-timer">
                <span className="sponsorship-card-early-timer-prefix">⏳</span>{" "}
                <span className="sponsorship-card-early-timer-strong">Live now</span>
            </p>
        );
    }

    const diff = parsed.getTime() - now;
    return (
        <p className="sponsorship-card-early-timer">
            <span className="sponsorship-card-early-timer-prefix">⏳ Opens in </span>
            <span className="sponsorship-card-early-timer-strong">{formatCountdownHms(diff)}</span>
        </p>
    );
};

export type SponsorshipCampaignCardProps = {
    campaign: Campaign;
    /** Numeric amount shown as ₹… in the footer (budget, barter value, etc.). */
    proBadge?: boolean;
    /** Optional content inside the image area, after the PRO badge (if any) and before the hero image. */
    imageOverlay?: React.ReactNode;
    /** Optional extra content below the title / category block. */
    children?: React.ReactNode;
    className?: string;
    /** Tall card with blue gradient footer, countdown / “Live now”, optional applicants line (Early access). */
    earlyAccess?: boolean;
    /** ISO date or timestamp for “Opens in” countdown; if already passed, shows “Live now”. */
    earlyAccessOpensAt?: string | number | Date | null;
    /** When &gt; 0, shows “🔥 N Pro creators already applied”. */
    earlyAccessApplicantsCount?: number;
    isInvited?: boolean;
};

const SponsorshipCampaignCard: React.FC<SponsorshipCampaignCardProps> = ({
    campaign,
    proBadge,
    imageOverlay,
    children,
    className,
    earlyAccess,
    earlyAccessOpensAt,
    earlyAccessApplicantsCount,
    isInvited,
}) => {
    const rootClass = ["sponsorship-card", earlyAccess && "sponsorship-card--early-access", className].filter(Boolean).join(" ");

    const inner = (
        <>
            <div className="sponsorship-card-image-wrap">
                {proBadge ? (
                    <div className="sponsorship-card-pro-badge" aria-hidden>
                        <span className="sponsorship-card-pro-badge-label">PRO</span>
                        <span className="sponsorship-card-pro-badge-suffix">members only</span>
                    </div>
                ) : null}
                {imageOverlay}
                <img className="sponsorship-card-image" src={campaign.hero_img_url} alt={campaign.name} />
                <div className="sponsorship-card-platforms">
                    {campaign.platform === "ig" && (
                        <span className="sponsorship-platform-icon">
                            <img src={instagramLogo as unknown as string} alt="Instagram" className="sponsorship-platform-logo" />
                        </span>
                    )}
                    {campaign.platform === "yt" && (
                        <span className="sponsorship-platform-icon">
                            <img src={youtubeLogo as unknown as string} alt="YouTube" className="sponsorship-platform-logo" />
                        </span>
                    )}
                </div>
            </div>

            <div className="sponsorship-card-content">
                <h4 className="sponsorship-card-name">{campaign.name}</h4>
                {campaign?.brand?.category ? <p className="sponsorship-card-category">{campaign?.brand?.category}</p> : null}
                {children}
            </div>
            {
                isInvited ? (
                    <div className="sponsorship-card-invited-footer">
                        <Link to={`/campaign/${campaign.uuid}`} className="w-100 btn btn-sm btn-primary">VIEW INVITE</Link>
                    </div>
                )
                    : (
                        <div className="sponsorship-card-footer">
                            <p className={`sponsorship-card-budget ${toNumber(campaign.budget) > 0 ? 'text-success' : ''}`}>{toNumber(campaign.budget) > 0 ? `₹${formatNumber(toNumber(campaign.budget ?? 0))}` : 'Barter'}</p>
                            <div className="sponsorship-card-tier">
                                <CreatorCategoryIcon />
                                <span>{getTier(campaign)}</span>
                            </div>
                            <p className="sponsorship-card-seats">{getSeatsLabel(campaign)}</p>
                        </div>
                    )
            }
        </>
    );

    if (earlyAccess) {
        const showApplicants = typeof earlyAccessApplicantsCount === "number" && earlyAccessApplicantsCount > 0;

        return (
            <Link to={`/campaign/${campaign.uuid}`} className={rootClass}>
                <div className="sponsorship-card-early-stripes" aria-hidden>
                    <span className="sponsorship-card-early-stripe sponsorship-card-early-stripe--a" />
                    <span className="sponsorship-card-early-stripe sponsorship-card-early-stripe--b" />
                </div>
                <div className="sponsorship-card-early-inner">{inner}</div>
                <div className="sponsorship-card-early-bottom">
                    {showApplicants ? (
                        <p className="sponsorship-card-early-applicants">
                            <span className="sponsorship-card-early-applicants-emoji">🔥 </span>
                            <span className="sponsorship-card-early-applicants-strong">{earlyAccessApplicantsCount} Pro</span>
                            <span> creators already applied</span>
                        </p>
                    ) : null}
                    <EarlyAccessTimer target={earlyAccessOpensAt} />
                </div>
            </Link>
        );
    }

    return (
        <Link to={`/campaign/${campaign.uuid}`} className={rootClass}>
            {inner}
        </Link>
    );
};

export default SponsorshipCampaignCard;
